import React, { useEffect } from 'react';
import useMeicWebsocketDataStore from '../Stores/MEIC_Data_Store';

import { MEIC_WS_URL } from './websocketUrls';
import {
  buildMeicAuthPayload,
  getAuthMode,
} from './buildAuthPayload';

/**
 * MEIC WebSocket client
 *
 * Auth protocol:
 *   Client -> Server:
 *     { type: 'auth', memberId: string }
 *
 *   Server -> Client:
 *     { type: 'auth_ok' }
 *     { type: 'auth_error', reason: 'AUTH_TIMEOUT' | 'INVALID_MEMBER' }
 *     { type: 'pong' }
 *     { type: 'meic_initial_state', tradeDate, trades }
 *     { type: 'market_state', state }
 *     { type: 'trade_opened', trade }
 *     { type: 'trade_update', trade }
 *     { type: 'daily_summary', ...summary }
 */
const MeicWebsocketClient = ({ memberCredentials }) => {
  const {
    upsertTrade,
    setConnectionStatus,
    setMarketState,
    setInitialTrades,
    setDailySummary,
  } = useMeicWebsocketDataStore();

  useEffect(() => {
    let ws = null;
    let reconnectTimeout = null;
    let heartbeatInterval = null;
    let authFailed = false;
    let manuallyClosed = false;

    const wsUrl = MEIC_WS_URL;

    async function buildAuthMessage() {
      /*
        Preferred production/local-switch path:
        - manual mode uses local env credentials from buildAuthPayload.js
        - memberstack mode uses logged-in Memberstack user
        - MEIC receives only { type: 'auth', memberId }
      */
      const authPayload = await buildMeicAuthPayload();

      /*
        Backward compatibility:
        If this component is still passed memberCredentials manually from
        somewhere else, allow it to override memberId without removing that
        existing prop path.
      */
      const fallbackMemberId =
        memberCredentials?.memberId ||
        memberCredentials?.id ||
        memberCredentials?.member_id ||
        null;

      return {
        type: 'auth',
        memberId: fallbackMemberId || authPayload.memberId,
      };
    }

    const connectWebSocket = () => {
      if (manuallyClosed || authFailed) {
        return;
      }

      if (!wsUrl) {
        console.error('[MEIC WS] No MEIC WebSocket URL configured');
        setConnectionStatus && setConnectionStatus('error');
        return;
      }

      setConnectionStatus && setConnectionStatus('connecting');

      ws = new WebSocket(wsUrl);

      ws.onopen = async () => {
        try {
          const authMessage = await buildAuthMessage();

          if (!authMessage.memberId) {
            throw new Error('Missing MEIC memberId for websocket auth.');
          }

          // console.log('[MEIC WS] Connected. Sending auth:', {
          //   mode: getAuthMode(),
          //   type: authMessage.type,
          //   memberId: authMessage.memberId,
          // });

          ws.send(JSON.stringify(authMessage));
        } catch (err) {
          console.error('[MEIC WS] Failed to build/send auth message:', err);

          authFailed = true;
          setConnectionStatus && setConnectionStatus('auth_error');

          if (
            ws &&
            (ws.readyState === WebSocket.OPEN ||
              ws.readyState === WebSocket.CONNECTING)
          ) {
            ws.close();
          }
        }
      };

      ws.onmessage = event => {
        let msg;

        try {
          msg = JSON.parse(event.data);
        } catch (err) {
          console.warn('[MEIC WS] Non-JSON message ignored:', event.data);
          return;
        }

        const { type } = msg;

        // console.log('[MEIC WS] Incoming message type:', type, msg);

        switch (type) {
          case 'auth_ok': {
            setConnectionStatus && setConnectionStatus('connected');

            if (!heartbeatInterval) {
              heartbeatInterval = setInterval(() => {
                if (ws && ws.readyState === WebSocket.OPEN) {
                  try {
                    ws.send(JSON.stringify({ type: 'ping' }));
                  } catch (_) {}
                }
              }, 30000);
            }

            break;
          }

          case 'auth_error': {
            console.warn('[MEIC WS] Auth error:', msg.reason);

            authFailed = true;
            setConnectionStatus && setConnectionStatus('auth_error');

            if (
              ws &&
              (ws.readyState === WebSocket.OPEN ||
                ws.readyState === WebSocket.CONNECTING)
            ) {
              ws.close();
            }

            break;
          }

          case 'pong': {
            break;
          }

          case 'meic_initial_state': {
            const { trades, tradeDate } = msg;

            if (!Array.isArray(trades)) {
              console.warn(
                '[MEIC WS] meic_initial_state without valid trades array:',
                msg
              );
              return;
            }

            // console.log('[MEIC WS] meic_initial_state raw:', {
            //   tradeDate,
            //   count: trades.length,
            //   firstRawTrade: trades[0] || null,
            //   lastRawTrade: trades[trades.length - 1] || null,
            // });

            const normalizedTrades = trades.map(row => ({
              id: String(row.id),

              optionType: row.option_type || row.optionType,

              shortStrike:
                row.short_strike != null
                  ? Number(row.short_strike)
                  : row.shortStrike != null
                  ? Number(row.shortStrike)
                  : null,

              longStrike:
                row.long_strike != null
                  ? Number(row.long_strike)
                  : row.longStrike != null
                  ? Number(row.longStrike)
                  : null,

              premium: row.premium != null ? Number(row.premium) : null,

              pnlDollars:
                row.net_profit_loss != null
                  ? Number(row.net_profit_loss)
                  : row.profit_loss != null
                  ? Number(row.profit_loss)
                  : null,

              entryTime:
                row.entry_timestamp_et ||
                row.entryTimestampEt ||
                row.entryTime ||
                null,

              status: row.outcome ? 'CLOSED' : row.status || 'OPEN',
              outcome: row.outcome || null,

              spreadMark:
                row.spread_mark != null
                  ? Number(row.spread_mark)
                  : row.spreadMark != null
                  ? Number(row.spreadMark)
                  : null,

              stopLevel:
                row.stop_level != null
                  ? Number(row.stop_level)
                  : row.stopLevel != null
                  ? Number(row.stopLevel)
                  : null,

              spxStopEstimateLinear:
                row.spx_stop_estimate_linear != null
                  ? Number(row.spx_stop_estimate_linear)
                  : row.spxStopEstimateLinear != null
                  ? Number(row.spxStopEstimateLinear)
                  : null,

              spxLevel:
                row.spx_level != null
                  ? Number(row.spx_level)
                  : row.spxLevel != null
                  ? Number(row.spxLevel)
                  : null,

              reason: row.reason || null,
            }));

            const closedTrades = normalizedTrades.filter(trade => {
              return (
                String(trade?.status || '').toUpperCase() === 'CLOSED' ||
                Boolean(trade?.outcome)
              );
            });

            const closedPnl = closedTrades.reduce((sum, trade) => {
              return sum + Number(trade?.pnlDollars || 0);
            }, 0);

            // console.log('[MEIC WS] meic_initial_state normalized:', {
            //   tradeDate,
            //   totalTrades: normalizedTrades.length,
            //   closedTrades: closedTrades.length,
            //   closedPnl,
            //   firstNormalizedTrade: normalizedTrades[0] || null,
            //   lastNormalizedTrade:
            //     normalizedTrades[normalizedTrades.length - 1] || null,
            // });

            if (setInitialTrades) {
              setInitialTrades({
                tradeDate,
                trades: normalizedTrades,
              });

              // console.log('[MEIC WS] setInitialTrades called:', {
              //   tradeDate,
              //   totalTrades: normalizedTrades.length,
              //   closedTrades: closedTrades.length,
              //   closedPnl,
              // });
            } else {
              console.warn(
                '[MEIC WS] setInitialTrades not defined in store; snapshot ignored'
              );
            }

            break;
          }

          case 'market_state': {
            const { state } = msg;

            if (!state || typeof state.regime === 'undefined') {
              console.warn('[MEIC WS] market_state without valid payload:', msg);
              return;
            }

            setMarketState && setMarketState(state);
            break;
          }

          case 'trade_opened': {
            const trade = msg.trade;

            if (!trade || !trade.id) {
              console.warn('[MEIC WS] trade_opened without valid trade payload:', msg);
              return;
            }

            const normalized = {
              id: String(trade.id),
              optionType: trade.optionType || trade.option_type || null,

              shortStrike:
                trade.shortStrike != null
                  ? Number(trade.shortStrike)
                  : trade.short_strike != null
                  ? Number(trade.short_strike)
                  : null,

              longStrike:
                trade.longStrike != null
                  ? Number(trade.longStrike)
                  : trade.long_strike != null
                  ? Number(trade.long_strike)
                  : null,

              premium: trade.premium != null ? Number(trade.premium) : null,

              width: trade.width != null ? Number(trade.width) : null,

              entryTime:
                trade.entryTimestampEt ||
                trade.entryTime ||
                trade.entry_timestamp_et ||
                null,

              pnlDollars: null,
              status: trade.status || 'OPEN',
              outcome: trade.outcome || null,
              spxAtEntry: trade.spxAtEntry ?? trade.spx_at_entry ?? null,
            };

            console.log('[MEIC WS] trade_opened normalized:', normalized);

            upsertTrade && upsertTrade(normalized);
            break;
          }

          case 'trade_update': {
            const trade = msg.trade;

            if (!trade || !trade.id) {
              console.warn('[MEIC WS] trade_update without valid trade payload:', msg);
              return;
            }

            const normalizedUpdate = {
              id: String(trade.id),

              optionType: trade.optionType || trade.option_type,

              shortStrike:
                trade.shortStrike != null
                  ? Number(trade.shortStrike)
                  : trade.short_strike != null
                  ? Number(trade.short_strike)
                  : undefined,

              longStrike:
                trade.longStrike != null
                  ? Number(trade.longStrike)
                  : trade.long_strike != null
                  ? Number(trade.long_strike)
                  : undefined,

              premium:
                trade.premium != null ? Number(trade.premium) : undefined,

              width: trade.width != null ? Number(trade.width) : undefined,

              pnlDollars:
                trade.realizedPnlDollars != null
                  ? Number(trade.realizedPnlDollars)
                  : trade.currentPnlDollars != null
                  ? Number(trade.currentPnlDollars)
                  : trade.pnlDollars != null
                  ? Number(trade.pnlDollars)
                  : undefined,

              status: trade.status,
              outcome: trade.outcome,
              reason: trade.reason,

              spreadMark:
                trade.spreadMark != null ? Number(trade.spreadMark) : undefined,

              stopLevel:
                trade.stopLevel != null ? Number(trade.stopLevel) : undefined,

              spxStopEstimateLinear:
                trade.spxStopEstimateLinear != null
                  ? Number(trade.spxStopEstimateLinear)
                  : undefined,

              spxLevel:
                trade.spxLevel != null ? Number(trade.spxLevel) : undefined,

              etTimeStr: trade.etTimeStr || null,
            };

            if (
              String(normalizedUpdate.status || '').toUpperCase() === 'CLOSED' ||
              normalizedUpdate.outcome
            ) {
              console.log('[MEIC WS] trade_update CLOSED normalized:', normalizedUpdate);
            }

            upsertTrade && upsertTrade(normalizedUpdate);
            break;
          }

          case 'daily_summary': {
            console.log('[MEIC WS] daily_summary received by client:', msg);

            if (setDailySummary) {
              setDailySummary(msg);

              console.log('[MEIC WS] setDailySummary called:', {
                totalRealizedPnlDollars: msg.totalRealizedPnlDollars,
                totalTradesClosed: msg.totalTradesClosed,
                date: msg.date,
              });
            } else {
              console.warn(
                '[MEIC WS] setDailySummary not defined in store; summary ignored',
                msg
              );
            }

            break;
          }

          default: {
            console.warn('[MEIC WS] Unrecognized message type:', type, msg);
          }
        }
      };

      ws.onclose = event => {
        console.log('[MEIC WS] Connection closed:', event);

        if (heartbeatInterval) {
          clearInterval(heartbeatInterval);
          heartbeatInterval = null;
        }

        ws = null;

        if (manuallyClosed) {
          setConnectionStatus && setConnectionStatus('disconnected');
          return;
        }

        if (authFailed) {
          setConnectionStatus && setConnectionStatus('auth_error');
          return;
        }

        setConnectionStatus && setConnectionStatus('disconnected');

        scheduleReconnect();
      };

      ws.onerror = error => {
        console.error('[MEIC WS] WebSocket error:', error);
      };
    };

    const scheduleReconnect = () => {
      if (reconnectTimeout || manuallyClosed || authFailed) return;

      reconnectTimeout = setTimeout(() => {
        connectWebSocket();
        reconnectTimeout = null;
      }, 1000);
    };

    const cleanupWebSocket = () => {
      manuallyClosed = true;

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }

      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
      }

      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;

        if (
          ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING
        ) {
          ws.close();
        }

        ws = null;
      }

      setConnectionStatus && setConnectionStatus('disconnected');
    };

    connectWebSocket();

    return () => {
      cleanupWebSocket();
    };
  }, [
    memberCredentials,
    upsertTrade,
    setConnectionStatus,
    setMarketState,
    setInitialTrades,
    setDailySummary,
  ]);

  return null;
};

export default MeicWebsocketClient;
